import { STORAGE_KEY } from '@hooks/useLocalStorage';
import log from '@src/datadog';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { BLACKLIST_AUTH_PATH, HTTPMethods } from './constants';
import UserManagerClient from './UserManagerClient';

interface ParamsProps<T1 = null, T2 = null> {
  path: string;
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  params?: T1 | T2;
  data?: T1;
}

type AxiosOptionalConfig = Omit<
  AxiosRequestConfig,
  'url' | 'method' | 'data' | 'params'
>;

const ensureError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(String(error));
};

const createOnResponseError =
  () =>
  async (error: AxiosError): Promise<AxiosError> => {
    if (error?.response?.status === 401) {
      const user = await UserManagerClient.getUser();

      localStorage.removeItem(STORAGE_KEY.MARKETING_ID());

      await UserManagerClient.removeUser();
      await UserManagerClient.revokeTokens(['access_token', 'refresh_token']);
      await UserManagerClient.clearStaleState();

      if (user?.id_token) {
        await UserManagerClient.signoutRedirect({
          post_logout_redirect_uri: window.location.origin,
          id_token_hint: user.id_token,
        });
      } else {
        await UserManagerClient.signinRedirect();
      }

      log.clearDDUser();
    } else {
      log.sendAxiosError(error);
    }

    return Promise.reject(ensureError(error));
  };

class Service {
  private readonly httpService: AxiosInstance;
  private readonly baseURLResolver: string | (() => string);

  constructor(baseURL: string | (() => string)) {
    this.baseURLResolver = baseURL;

    const resolvedBaseURL = typeof baseURL === 'function' ? baseURL() : baseURL;

    this.httpService = axios.create({
      baseURL: resolvedBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.httpService.interceptors.request.use(
      (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        // Re-resolve baseURL on each request if it's a function
        if (typeof this.baseURLResolver === 'function') {
          request.baseURL = this.baseURLResolver();
        }
        return request;
      },
      (error: AxiosError): Promise<AxiosError> =>
        Promise.reject(ensureError(error)),
    );

    this.httpService.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => response,
      (error: AxiosError): Promise<AxiosError> =>
        createOnResponseError()(error),
    );
  }

  async fetchAPI<Params, Responses, OptionalParams = null>(
    parameters: ParamsProps<Params, OptionalParams>,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const { path: url, method, data, params } = parameters;

    const { headers, responseType } = options ?? {};

    const requestOptions: AxiosRequestConfig = {
      url,
      method,
      data: data ?? null,
      params: params ?? null,
      headers: {},
      responseType: responseType ?? 'json',
    };

    let accessToken = import.meta.env.VITE_KEYCLOAK_MOCK_NIK ?? '';

    if (import.meta.env.VITE_KEYCLOAK_ENABLE) {
      const user = await UserManagerClient.getUser();
      accessToken = user?.access_token ?? '';
    }

    const selectedMarketingId = localStorage.getItem(
      STORAGE_KEY.MARKETING_ID(),
    );
    if (/agency\//.exec(url)?.length && selectedMarketingId) {
      requestOptions.headers = {
        ...requestOptions.headers,
        'x-marketing-id': selectedMarketingId.replaceAll('"', ''),
      };
    }

    if (
      accessToken &&
      !BLACKLIST_AUTH_PATH.find(pathname => url.startsWith(pathname))
    ) {
      requestOptions.headers =
        import.meta.env.VITE_KEYCLOAK_ENABLE !== 'true'
          ? {
              ...requestOptions.headers,
              'x-nik': accessToken,
            }
          : {
              ...requestOptions.headers,
              Authorization: `Bearer ${accessToken}`,
            };
    }

    requestOptions.headers = { ...requestOptions.headers, ...headers };

    return await this.httpService(requestOptions);
  }

  async get<Params, Responses>(
    path: string,
    params?: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Get;
    return await this.fetchAPI<typeof params, Responses>(
      {
        path,
        method,
        params,
      },
      options,
    );
  }

  async post<Params, Responses>(
    path: string,
    data: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Post;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
      },
      options,
    );
  }

  async patch<Datas, Responses>(
    path: string,
    data: Datas,
    params?: Datas,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Patch;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
        params,
      },
      options,
    );
  }

  async put<Datas, Responses, Params = null>(
    path: string,
    data?: Datas,
    params?: Datas | Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Put;
    return await this.fetchAPI<typeof data, Responses, Params>(
      {
        path,
        method,
        data,
        params,
      },
      options,
    );
  }

  async delete<Params, Responses>(
    path: string,
    data: Params,
    options?: AxiosOptionalConfig,
  ): Promise<AxiosResponse<Responses>> {
    const method = HTTPMethods.Delete;
    return await this.fetchAPI<typeof data, Responses>(
      {
        path,
        method,
        data,
      },
      options,
    );
  }
}

export default Service;
